/**
 * Performance Management System — admin / supervisor console.
 *
 * Cycle picker + team scorecard table. HR (can_manage_performance / master)
 * can create cycles, enrol staff and move the cycle stage. Supervisors see
 * only the staff whose `reporting_manager_id` points at them.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Target, Plus, Loader2, Users, ChevronRight, RefreshCw, TrendingUp,
  ClipboardCheck, CalendarRange, Trash2, Mail, Send, Scale, LifeBuoy, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { formatErr, inputCls } from '../../../components/admin/billing/shared';
import { BAND_STYLE, STATUS_STYLE, statusLabel, Badge, ScoreRing } from './shared';
import SkillBadge from '../../../components/admin/SkillBadge';
import ModuleGuide from './ModuleGuide';
import { DriveIQBadge } from '../../../components/admin/DriveIQ';

const PerformancePage = () => {
  const { authHeader } = useAuth();
  const [meta, setMeta] = useState(null);
  const [cycles, setCycles] = useState([]);
  const [cycleId, setCycleId] = useState('');
  const [board, setBoard] = useState({ cycle: null, rows: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [newCycle, setNewCycle] = useState(null); // {fy_start_year, half}

  const isHr = !!meta?.capabilities?.is_hr;

  const loadMeta = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/performance/meta`, { headers: authHeader() });
      setMeta(data);
    } catch (e) { setErr(formatErr(e)); }
  }, [authHeader]);

  const loadCycles = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/performance/cycles`, { headers: authHeader() });
      setCycles(data.cycles || []);
      setCycleId((prev) => prev || (data.cycles?.[0]?.id || ''));
    } catch (e) { setErr(formatErr(e)); }
  }, [authHeader]);

  const loadBoard = useCallback(async (cid) => {
    setLoading(true);
    try {
      const qs = cid ? `?cycle_id=${cid}` : '';
      const { data } = await axios.get(`${API}/performance/dashboard${qs}`, { headers: authHeader() });
      setBoard(data || { cycle: null, rows: [], summary: {} });
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  }, [authHeader]);

  useEffect(() => { loadMeta(); loadCycles(); }, [loadMeta, loadCycles]);
  useEffect(() => { loadBoard(cycleId); }, [cycleId, loadBoard]);

  const activeCycle = useMemo(
    () => cycles.find((c) => c.id === cycleId) || board.cycle,
    [cycles, cycleId, board.cycle],
  );

  const createCycle = async () => {
    if (!newCycle) return;
    setBusy('create');
    try {
      const { data } = await axios.post(`${API}/performance/cycles`, {
        fy_start_year: Number(newCycle.fy_start_year),
        half: newCycle.half,
      }, { headers: authHeader() });
      toast.success(`Cycle "${data.name}" created`);
      setNewCycle(null);
      await loadCycles();
      setCycleId(data.id);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  const emailAppraisals = async () => {
    const cid = board.cycle?.id || cycleId;
    if (!cid) return;
    setBusy('email-all');
    try {
      const { data } = await axios.post(`${API}/performance/cycles/${cid}/email-appraisals`,
        {}, { headers: authHeader() });
      if (!data.considered) {
        toast.info('Nothing to send — every published appraisal in this cycle has already been emailed.');
      } else if (data.failed) {
        toast.error(`Sent ${data.sent} of ${data.considered}. ${data.failed_rows[0]?.staff_name || ''}: ${data.failed_rows[0]?.error || 'failed'}`);
      } else {
        toast.success(`Emailed ${data.sent} appraisal${data.sent === 1 ? '' : 's'} to staff`);
      }
      await loadBoard(cid);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  const enroll = async () => {
    if (!cycleId) return;
    setBusy('enroll');
    try {
      const { data } = await axios.post(`${API}/performance/cycles/${cycleId}/enroll`, {
        use_template: true,
      }, { headers: authHeader() });
      toast.success(`${data.created} staff enrolled with the default goal sheet`);
      await Promise.all([loadCycles(), loadBoard(cycleId)]);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  const sendReminders = async () => {
    setBusy('reminders');
    try {
      const { data } = await axios.post(`${API}/performance/reminders/send`, {}, { headers: authHeader() });
      if (data.skipped) toast.info(`Nothing to remind: ${data.reason}`);
      else if (data.error) toast.error(`${data.error} · ${data.sent} sent, ${data.failed} failed · pending: ${data.pending_self} self, ${data.pending_manager} manager, ${data.pending_approval} approval`);
      else toast.success(`${data.sent} reminder email(s) sent · ${data.pending_self} self, ${data.pending_manager} manager, ${data.pending_approval} approval pending`);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  const setStage = async (stage) => {
    setBusy('stage');
    try {
      await axios.patch(`${API}/performance/cycles/${cycleId}`, { stage }, { headers: authHeader() });
      toast.success(`Cycle moved to ${statusLabel(stage)}`);
      await loadCycles();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  const removeCycle = async () => {
    if (!window.confirm('Delete this cycle and all its (unpublished) review sheets?')) return;
    setBusy('delete');
    try {
      await axios.delete(`${API}/performance/cycles/${cycleId}`, { headers: authHeader() });
      toast.success('Cycle deleted');
      setCycleId('');
      await loadCycles();
      await loadBoard('');
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  const s = board.summary || {};

  return (
    <div className="space-y-5" data-testid="performance-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Target size={20} className="text-teal-600" /> Performance Management System
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Half-yearly goals, live metrics and appraisals for the workshop team.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            className={`${inputCls} !w-auto min-w-[220px]`}
            value={cycleId}
            onChange={(e) => setCycleId(e.target.value)}
            data-testid="cycle-select"
          >
            {!cycles.length && <option value="">No cycles yet</option>}
            {cycles.map((c) => (
              <option key={c.id} value={c.id}>{c.name} · {statusLabel(c.stage)}</option>
            ))}
          </select>
          <button
            onClick={() => loadBoard(cycleId)}
            className="p-2 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50"
            data-testid="refresh-board-btn"
            title="Refresh"
          >
            <RefreshCw size={15} />
          </button>
          {isHr && (
            <>
              <button
                onClick={sendReminders}
                disabled={busy === 'reminders'}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                data-testid="send-reminders-btn"
              >
                {busy === 'reminders' ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />}
                Send reminders
              </button>
              <button
                onClick={emailAppraisals} disabled={busy === 'email-all' || !(board.cycle?.id || cycleId)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-teal-300 bg-teal-50 text-sm text-teal-800 hover:bg-teal-100 disabled:opacity-50"
                title="Email every published appraisal in this cycle to the staff member, PDF attached"
                data-testid="email-appraisals-btn"
              >
                {busy === 'email-all' ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                Email appraisals
              </button>
              <button
                onClick={() => setNewCycle(meta?.cycle_suggestions?.[0]
                  ? { fy_start_year: meta.cycle_suggestions[0].fy_start_year, half: meta.cycle_suggestions[0].half }
                  : { fy_start_year: new Date().getFullYear(), half: 'H2' })}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-800"
                data-testid="new-cycle-btn"
              >
                <Plus size={15} /> New cycle
              </button>
              <button
                onClick={enroll}
                disabled={!cycleId || busy === 'enroll'}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-teal-600 text-white text-sm hover:bg-teal-700 disabled:opacity-50"
                data-testid="enroll-staff-btn"
              >
                {busy === 'enroll' ? <Loader2 size={15} className="animate-spin" /> : <Users size={15} />}
                Enrol staff
              </button>
            </>
          )}
        </div>
      </div>

      {err && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm" data-testid="performance-error">{err}</div>
      )}

      <ModuleGuide meta={meta} />

      {newCycle && (
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="new-cycle-panel">
          <h2 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-1.5">
            <CalendarRange size={15} className="text-teal-600" /> Open a new review cycle
          </h2>
          <div className="grid gap-3 sm:grid-cols-4">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Half</label>
              <select
                className={inputCls}
                value={newCycle.half}
                onChange={(e) => setNewCycle({ ...newCycle, half: e.target.value })}
                data-testid="new-cycle-half"
              >
                <option value="H1">H1 — Apr to Sep</option>
                <option value="H2">H2 — Oct to Mar</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Financial year (April start)</label>
              <input
                type="number" className={inputCls} value={newCycle.fy_start_year}
                onChange={(e) => setNewCycle({ ...newCycle, fy_start_year: e.target.value })}
                data-testid="new-cycle-year"
              />
            </div>
            <div className="sm:col-span-2 flex items-end gap-2">
              <button
                onClick={createCycle} disabled={busy === 'create'}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-teal-600 text-white text-sm hover:bg-teal-700 disabled:opacity-50"
                data-testid="save-cycle-btn"
              >
                {busy === 'create' ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Create
              </button>
              <button onClick={() => setNewCycle(null)} className="px-4 py-2 rounded-lg border border-slate-300 text-sm text-slate-600">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {activeCycle && (
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-slate-900">{activeCycle.name}</div>
              <div className="text-xs text-slate-500">
                {activeCycle.period_start} → {activeCycle.period_end} · {activeCycle.review_count ?? board.rows.length} review sheets
              </div>
            </div>
            {isHr && (
              <div className="flex items-center gap-2">
                <select
                  className={`${inputCls} !w-auto`} value={activeCycle.stage || 'goal_setting'}
                  onChange={(e) => setStage(e.target.value)} data-testid="cycle-stage-select"
                >
                  {(meta?.stages || []).map((st) => <option key={st} value={st}>{statusLabel(st)}</option>)}
                </select>
                <button
                  onClick={removeCycle}
                  className="p-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                  data-testid="delete-cycle-btn" title="Delete cycle"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-4">
            {[
              { label: 'Team members', value: s.total ?? 0, testid: 'stat-total' },
              { label: 'Avg final score', value: `${s.avg_final_score ?? 0}`, testid: 'stat-avg' },
              { label: 'Self review pending', value: s.pending_self ?? 0, testid: 'stat-pending-self' },
              { label: 'Manager review pending', value: s.pending_manager ?? 0, testid: 'stat-pending-manager' },
              { label: 'Awaiting approval', value: s.pending_approval ?? 0, testid: 'stat-pending-approval' },
            ].map((k) => (
              <div key={k.label} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2" data-testid={k.testid}>
                <div className="text-[11px] uppercase tracking-wider text-slate-500">{k.label}</div>
                <div className="text-lg font-semibold text-slate-900">{k.value}</div>
              </div>
            ))}
          </div>

          {!!Object.keys(s.by_band || {}).length && (
            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
              <span className="text-slate-500 flex items-center gap-1"><TrendingUp size={13} /> Band spread:</span>
              {(meta?.bands || []).map((b) => (
                <span key={b.band} className={`px-2 py-1 rounded-lg border ${BAND_STYLE[b.band]}`}>
                  {b.band} · {s.by_band?.[b.band] || 0}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 flex flex-wrap items-center gap-2">
          <ClipboardCheck size={15} className="text-teal-600" />
          <h2 className="text-sm font-semibold text-slate-800">Team scorecard</h2>
          <div className="ml-auto flex items-center gap-2">
            <Link
              to="/admin/performance/team-analysis"
              className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-teal-300 bg-teal-50 text-teal-800 hover:bg-teal-100"
              data-testid="team-analysis-link"
            >
              <Sparkles size={13} /> Team summary
            </Link>
            <DriveIQBadge className="hidden sm:inline-flex" testId="performance-driveiq" />
            <Link
              to="/admin/performance/calibration"
              className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
              data-testid="calibration-link"
            >
              <Scale size={13} /> Calibration
            </Link>
            <Link
              to="/admin/performance/plans"
              className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
              data-testid="plans-link"
            >
              <LifeBuoy size={13} /> Improvement plans
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="p-10 flex justify-center text-slate-400"><Loader2 className="animate-spin" /></div>
        ) : !board.rows.length ? (
          <div className="p-8 text-center text-sm text-slate-500" data-testid="no-reviews">
            {cycleId
              ? 'No review sheets in this cycle yet. Use "Enrol staff" to create them.'
              : 'Create a review cycle to get started.'}
          </div>
        ) : (
          <>
            {/* Desktop */}
            <table className="w-full text-sm hidden md:table" data-testid="reviews-table">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2">Staff</th>
                  <th className="text-left px-4 py-2">Reporting to</th>
                  <th className="text-right px-4 py-2">Goals</th>
                  <th className="text-right px-4 py-2">Behaviour</th>
                  <th className="text-right px-4 py-2">Final</th>
                  <th className="text-center px-4 py-2">Band</th>
                  <th className="text-left px-4 py-2">Stage</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {board.rows.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50" data-testid={`review-row-${r.id}`}>
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-slate-900 flex items-center gap-1.5">
                        {r.staff_name}
                        <SkillBadge staffId={r.staff_id} authHeader={authHeader} />
                      </div>
                      <div className="text-xs text-slate-500">{r.staff_role || '—'}</div>
                    </td>
                    <td className="px-4 py-2.5 text-slate-600 text-xs">{r.reporting_manager_name || '—'}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{r.goal_score}%</td>
                    <td className="px-4 py-2.5 text-right font-mono">{r.competency_score}%</td>
                    <td className="px-4 py-2.5 text-right font-mono font-semibold">{r.final_score}</td>
                    <td className="px-4 py-2.5 text-center">
                      <Badge className={BAND_STYLE[r.band]} testid={`band-${r.id}`}>{r.band}</Badge>
                    </td>
                    <td className="px-4 py-2.5">
                      <Badge className={STATUS_STYLE[r.status] || 'bg-slate-100 text-slate-600'}>
                        {statusLabel(r.status)}
                      </Badge>
                      {r.status === 'published' && (
                        r.acknowledged_at ? (
                          <span
                            className="ml-1.5 rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-800"
                            title={`Acknowledged by the employee on ${String(r.acknowledged_at).replace('T', ' ').slice(0, 16)}`}
                            data-testid={`ack-badge-${r.id}`}
                          >
                            Acknowledged
                          </span>
                        ) : (
                          <span
                            className="ml-1.5 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-800"
                            title="Waiting for the employee to confirm the appraisal was discussed"
                            data-testid={`ack-pending-${r.id}`}
                          >
                            Awaiting sign-off
                          </span>
                        )
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <Link
                        to={`/admin/performance/reviews/${r.id}`}
                        className="inline-flex items-center gap-1 text-teal-700 hover:text-teal-900 text-xs font-medium"
                        data-testid={`open-review-${r.id}`}
                      >
                        Open <ChevronRight size={13} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile */}
            <div className="md:hidden divide-y divide-slate-100">
              {board.rows.map((r) => (
                <Link
                  key={r.id} to={`/admin/performance/reviews/${r.id}`}
                  className="flex items-center gap-3 px-4 py-3 active:bg-slate-50"
                  data-testid={`review-card-${r.id}`}
                >
                  <ScoreRing value={r.final_score} band={r.band} size={54} />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-slate-900 truncate">{r.staff_name}</div>
                    <div className="text-xs text-slate-500 truncate">{r.staff_role || '—'} · to {r.reporting_manager_name || 'unassigned'}</div>
                    <div className="mt-1 flex items-center gap-1.5">
                      <Badge className={BAND_STYLE[r.band]}>{r.band}</Badge>
                      <Badge className={STATUS_STYLE[r.status] || 'bg-slate-100 text-slate-600'}>{statusLabel(r.status)}</Badge>
                    </div>
                  </div>
                  <ChevronRight size={16} className="text-slate-400" />
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PerformancePage;
